import { useState, useMemo } from 'react'
import { DragDropContext, Droppable, Draggable, DropResult } from '@hello-pangea/dnd'
import { useSegments, useReorderSegments, useDeleteSegment } from '../../hooks/useSegments'
import { calculateSegmentPace, formatTime, formatPace } from '../../lib/calculations'
import { EditSegmentDialog } from './EditSegmentDialog'
import { Segment } from '../../types'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../ui/table'
import { Button } from '../ui/button'
import { GripVertical, Edit, Trash2 } from 'lucide-react'

interface SegmentsTableProps {
  runId: string
}

export function SegmentsTable({ runId }: SegmentsTableProps) {
  const { data: segments, isLoading } = useSegments(runId)
  const reorderMutation = useReorderSegments()
  const deleteMutation = useDeleteSegment()
  const [editingSegment, setEditingSegment] = useState<Segment | null>(null)

  const handleDragEnd = (result: DropResult) => {
    if (!result.destination || !segments) return

    const items = Array.from(segments)
    const [reorderedItem] = items.splice(result.source.index, 1)
    items.splice(result.destination.index, 0, reorderedItem)

    const segmentIds = items.map(item => item.id)
    reorderMutation.mutate({ runId, segmentIds })
  }

  const handleDelete = async (id: string) => {
    if (window.confirm('Are you sure you want to delete this segment?')) {
      await deleteMutation.mutateAsync(id)
    }
  }

  // Pre-calculate cumulative values to avoid re-computation on each render
  const segmentData = useMemo(() => {
    if (!segments) return []
    
    let cumulativeDistance = 0
    let cumulativeTime = 0
    
    return segments.map((segment, index) => {
      cumulativeDistance += segment.distance_km
      const pace = calculateSegmentPace(segment)
      const segmentTime = (pace * segment.distance_km) + (segment.planned_stop_min || 0)
      cumulativeTime += segmentTime
      
      return {
        ...segment,
        index,
        pace,
        segmentTime,
        cumulativeDistance,
        cumulativeTime
      }
    })
  }, [segments])

  // Calculate totals for summary row
  const totals = useMemo(() => {
    if (!segments || segments.length === 0) return null
    
    const totalDistance = segments.reduce((sum, segment) => sum + segment.distance_km, 0)
    const totalElevGain = segments.reduce((sum, segment) => sum + (segment.elev_gain_m || 0), 0)
    const totalElevLoss = segments.reduce((sum, segment) => sum + (segment.elev_loss_m || 0), 0)
    const totalTime = segmentData.reduce((sum, data) => sum + data.segmentTime, 0)
    const avgPace = totalTime / totalDistance
    
    return {
      totalDistance,
      totalElevGain,
      totalElevLoss,
      totalTime,
      avgPace
    }
  }, [segments, segmentData])

  if (isLoading) return <div>Loading segments...</div>
  if (!segments || segments.length === 0) {
    return (
      <div className="text-center py-8">
        <p className="text-gray-500">No segments yet. Add your first segment to get started.</p>
      </div>
    )
  }

  return (
    <DragDropContext onDragEnd={handleDragEnd}>
      {/* Desktop Table View */}
      <div className="hidden lg:block rounded-lg border overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="bg-muted/40">
              <TableHead className="w-[40px] font-semibold"></TableHead>
              <TableHead className="font-semibold">Label</TableHead>
              <TableHead className="font-semibold">Distance</TableHead>
              <TableHead className="font-semibold">Elev Gain</TableHead>
              <TableHead className="font-semibold">Elev Loss</TableHead>
              <TableHead className="font-semibold">Pace</TableHead>
              <TableHead className="font-semibold">Time</TableHead>
              <TableHead className="font-semibold">Cumulative</TableHead>
              <TableHead className="font-semibold">ETA</TableHead>
              <TableHead className="w-[100px] font-semibold">Actions</TableHead>
            </TableRow>
          </TableHeader>

        <Droppable droppableId="segments">
          {(provided) => (
            <TableBody {...provided.droppableProps} ref={provided.innerRef}>
              {segmentData.map((data, index) => (
                <Draggable key={data.id} draggableId={data.id} index={index}>
                  {(provided, snapshot) => (
                    <TableRow
                      ref={provided.innerRef}
                      {...provided.draggableProps}
                      className={`${snapshot.isDragging ? 'opacity-50' : ''} hover:bg-muted/50 h-12`}
                    >
                      <TableCell {...provided.dragHandleProps}>
                        <GripVertical className="h-4 w-4 text-gray-400" />
                      </TableCell>
                      <TableCell className="font-medium">{data.label || `Segment ${index + 1}`}</TableCell>
                      <TableCell>{data.distance_km} km</TableCell>
                      <TableCell>{data.elev_gain_m}m</TableCell>
                      <TableCell>{data.elev_loss_m}m</TableCell>
                      <TableCell className="font-mono">{formatPace(data.pace)} min/km</TableCell>
                      <TableCell className="font-mono">{formatTime(data.segmentTime)}</TableCell>
                      <TableCell className="text-muted-foreground">{data.cumulativeDistance.toFixed(2)} km</TableCell>
                      <TableCell className="font-mono text-muted-foreground">T+{formatTime(data.cumulativeTime)}</TableCell>
                      <TableCell>
                        <div className="flex space-x-2">
                          <Button 
                            variant="outline" 
                            size="sm"
                            onClick={() => setEditingSegment(data)}
                            className="h-8 w-8 p-0"
                            title="Edit segment"
                          >
                            <Edit className="h-4 w-4" />
                          </Button>
                          <Button 
                            variant="outline" 
                            size="sm"
                            onClick={() => handleDelete(data.id)}
                            className="h-8 w-8 p-0 hover:bg-destructive hover:text-destructive-foreground"
                            title="Delete segment"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  )}
                </Draggable>
              ))}
              {provided.placeholder}
              
              {/* Summary Row */}
              {totals && (
                <TableRow className="border-t-2 bg-muted/50 font-medium">
                  <TableCell></TableCell>
                  <TableCell className="font-semibold">Total</TableCell>
                  <TableCell className="font-semibold">{totals.totalDistance.toFixed(2)} km</TableCell>
                  <TableCell className="font-semibold">{totals.totalElevGain}m</TableCell>
                  <TableCell className="font-semibold">{totals.totalElevLoss}m</TableCell>
                  <TableCell className="font-mono font-semibold">{formatPace(totals.avgPace)} avg</TableCell>
                  <TableCell className="font-mono font-semibold">{formatTime(totals.totalTime)}</TableCell>
                  <TableCell></TableCell>
                  <TableCell></TableCell>
                  <TableCell></TableCell>
                </TableRow>
              )}
            </TableBody>
          )}
        </Droppable>
        </Table>
      </div>

      {/* Mobile Card View */}
      <div className="lg:hidden space-y-4">
        <Droppable droppableId="segments-mobile">
          {(provided) => (
            <div {...provided.droppableProps} ref={provided.innerRef} className="space-y-3">
              {segmentData.map((data, index) => (
                <Draggable key={data.id} draggableId={data.id} index={index}>
                  {(provided, snapshot) => (
                    <div
                      ref={provided.innerRef}
                      {...provided.draggableProps}
                      className={`rounded-lg border bg-card p-4 ${snapshot.isDragging ? 'opacity-50' : ''}`}
                    >
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center space-x-2">
                          <div {...provided.dragHandleProps}>
                            <GripVertical className="h-4 w-4 text-gray-400" />
                          </div>
                          <h3 className="font-medium text-lg">
                            {data.label || `Segment ${index + 1}`}
                          </h3>
                        </div>
                        <div className="flex space-x-2">
                          <Button 
                            variant="outline" 
                            size="sm"
                            onClick={() => setEditingSegment(data)}
                            className="h-8 w-8 p-0"
                            title="Edit segment"
                          >
                            <Edit className="h-4 w-4" />
                          </Button>
                          <Button 
                            variant="outline" 
                            size="sm"
                            onClick={() => handleDelete(data.id)}
                            className="h-8 w-8 p-0 hover:bg-destructive hover:text-destructive-foreground"
                            title="Delete segment"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                      
                      <div className="grid grid-cols-2 gap-4 text-sm">
                        <div>
                          <span className="text-muted-foreground">Distance:</span>
                          <p className="font-medium">{data.distance_km} km</p>
                        </div>
                        <div>
                          <span className="text-muted-foreground">Pace:</span>
                          <p className="font-mono font-medium">{formatPace(data.pace)} min/km</p>
                        </div>
                        <div>
                          <span className="text-muted-foreground">Elev Gain:</span>
                          <p className="font-medium">{data.elev_gain_m}m</p>
                        </div>
                        <div>
                          <span className="text-muted-foreground">Time:</span>
                          <p className="font-mono font-medium">{formatTime(data.segmentTime)}</p>
                        </div>
                        <div>
                          <span className="text-muted-foreground">Elev Loss:</span>
                          <p className="font-medium">{data.elev_loss_m}m</p>
                        </div>
                        <div>
                          <span className="text-muted-foreground">ETA:</span>
                          <p className="font-mono font-medium">T+{formatTime(data.cumulativeTime)}</p>
                        </div>
                      </div>
                      
                      <div className="mt-3 pt-3 border-t text-sm text-muted-foreground">
                        Cumulative: {data.cumulativeDistance.toFixed(2)} km
                      </div>
                    </div>
                  )}
                </Draggable>
              ))}
              {provided.placeholder}
            </div>
          )}
        </Droppable>
        
        {/* Mobile Summary Card */}
        {totals && (
          <div className="rounded-lg border bg-muted/20 p-4">
            <h3 className="font-semibold text-lg mb-3">Course Summary</h3>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <span className="text-muted-foreground">Total Distance:</span>
                <p className="font-semibold text-lg">{totals.totalDistance.toFixed(2)} km</p>
              </div>
              <div>
                <span className="text-muted-foreground">Total Time:</span>
                <p className="font-mono font-semibold text-lg">{formatTime(totals.totalTime)}</p>
              </div>
              <div>
                <span className="text-muted-foreground">Elev Gain:</span>
                <p className="font-semibold">{totals.totalElevGain}m</p>
              </div>
              <div>
                <span className="text-muted-foreground">Avg Pace:</span>
                <p className="font-mono font-semibold">{formatPace(totals.avgPace)} min/km</p>
              </div>
            </div>
          </div>
        )}
      </div>

      <EditSegmentDialog
        segment={editingSegment}
        open={!!editingSegment}
        onOpenChange={(open) => !open && setEditingSegment(null)}
      />
    </DragDropContext>
  )
}